import { init, id } from "@instantdb/react";
import { i } from "@instantdb/core";
import { shouldEnableInstantDbDevtools } from "./stores/developer";

// Re-export id for generating unique IDs
export { id };

// StyleOption type for project styles
export type StyleOption = string;

const APP_ID =
  import.meta.env.VITE_INSTANTDB_APP_ID ||
  "9f4165ab-f19e-40f4-80ec-0426b18ebcb4";

// Check if devtools should be enabled (reads from localStorage before Valtio init)
const ENABLE_DEVTOOLS = shouldEnableInstantDbDevtools();

// ============ Schema Types ============

/**
 * Project status values
 */
export type ProjectStatus =
  | "draft"
  | "uploading"
  | "processing"
  | "completed"
  | "failed";

/**
 * Flow type - pre-recorded mashup or live recording
 */
export type FlowType = "mashup" | "live";

/**
 * Upload status for videos
 */
export type UploadStatus = "pending" | "uploading" | "completed" | "failed";

/**
 * Processing job status
 */
export type JobStatus =
  | "queued"
  | "uploading"
  | "analytics"    // Running video analytics
  | "analyzing"    // Legacy alias for analytics
  | "rendering"    // Local/sync rendering
  | "transcoding"  // Async rendering (api.video/RunPod webhook-based)
  | "completed"
  | "failed";

/**
 * Camera guidance from AI for live workflow
 */
export interface CameraGuidance {
  cameraId: string;
  cameraName: string;
  shotType: string;
  description: string;
  priority: number;
}

// ============ Notification Types ============

/**
 * Notification type
 */
export type NotificationType =
  | "render_complete"
  | "render_failed"
  | "share_received"
  | "comment"
  | "system";

/**
 * Notification entity - in-app notifications
 */
export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  body: string;
  data: {
    projectId?: string;
    jobId?: string;
    actionUrl?: string;
  };
  read: boolean;
  createdAt: number;
}

// ============ Feature Flag Types ============

/**
 * Feature flag entity - A/B testing and gradual rollout
 */
export interface FeatureFlag {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  rolloutPercent: number;
  allowedUserIds: string[];
  blockedUserIds: string[];
  metadata: {
    variants?: string[];
    defaultVariant?: string;
  };
  createdAt: number;
  updatedAt: number;
}

// ============ User Presence Types ============

/**
 * User presence status
 */
export type PresenceStatus = "online" | "idle" | "offline";

/**
 * User presence entity - real-time user activity tracking
 */
export interface UserPresence {
  id: string;
  userId: string;
  status: PresenceStatus;
  currentProjectId?: string;
  currentAction?: string;
  lastSeen: number;
  deviceInfo: {
    platform: "web" | "ios" | "android" | "desktop";
    appVersion: string;
  };
}

// ============ Analytics Types ============

/**
 * Analytics session entity - tracks user sessions with device info
 */
export interface AnalyticsSession {
  id: string;
  userId: string; // Anonymous ID if not logged in
  sessionId: string; // Unique per browser session
  // Device info
  browser: string; // 'Chrome', 'Safari', 'Firefox', 'Edge', 'Other'
  browserVersion: string;
  deviceType: string; // 'mobile', 'tablet', 'desktop'
  platform: string; // 'iOS', 'Android', 'macOS', 'Windows', 'Linux', 'Web'
  osVersion: string;
  screenWidth: number;
  screenHeight: number;
  // App info
  appVersion: string;
  buildDate: string;
  buildTimestamp: number;
  // Session timing
  startedAt: number;
  endedAt?: number;
  durationSeconds?: number;
  // Metadata
  pageCount: number;
  errorCount: number;
}

/**
 * Analytics event entity - individual events (page views, errors, custom)
 */
export interface AnalyticsEvent {
  id: string;
  sessionId: string; // Links to analytics_sessions
  userId: string; // Anonymous ID if not logged in
  // Event info
  eventType: string; // 'page_view', 'error', 'custom'
  eventName: string; // Route path for page_view, error type, or custom name
  eventData?: Record<string, unknown>; // Additional event-specific data
  // Timing
  timestamp: number;
  // For page views
  routePath?: string;
  previousRoutePath?: string;
  timeOnPreviousPageSeconds?: number;
  // For errors
  errorMessage?: string;
  errorStack?: string;
  componentStack?: string;
}

// ============ Entity Types ============

/**
 * Project entity - stores all settings needed for processing
 */
export interface Project {
  id: string;
  userId: string;
  name: string;
  description: string;
  flow: FlowType;
  // Output settings
  style: StyleOption;
  duration: number; // 15, 30, 60
  orientation: "auto" | "portrait" | "landscape";
  frameRate: "auto" | "24" | "30" | "60";
  generateMusic: boolean;
  // Live workflow
  script?: string;
  cameraGuidance?: CameraGuidance[];
  // Status
  status: ProjectStatus;
  createdAt: number;
  updatedAt: number;
  thumbnail?: string;
  outputR2Key?: string;
}

/**
 * Project video entity - video files linked to projects
 */
export interface ProjectVideo {
  id: string;
  projectId: string;
  r2Key: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  duration?: number;
  order: number;
  uploadStatus: UploadStatus;
  uploadProgress?: number;
  errorMessage?: string;
  createdAt: number;
}

/**
 * Processing job entity - tracks job status in real-time
 */
export interface ProcessingJob {
  id: string;
  projectId: string;
  status: JobStatus;
  progress: number;
  message: string;
  startedAt: number;
  completedAt?: number;
  outputR2Key?: string;
  error?: string;
  analyticsJson?: Record<string, unknown>;
  edlJson?: Record<string, unknown>;
  // External job tracking (for async webhook-based rendering)
  externalJobId?: string;
  externalProvider?: string;
  musicKey?: string;
  userId?: string;
}

/**
 * Recording session status
 */
export type SessionStatus =
  | "pending"
  | "active"
  | "armed"
  | "recording"
  | "completed";

/**
 * Session device role
 */
export type DeviceRole = "master" | "slave";

/**
 * Session device status
 */
export type DeviceStatus =
  | "joined"
  | "armed"
  | "recording"
  | "uploading"
  | "completed"
  | "error";

/**
 * Recording session entity - for coordinated multi-device recording
 */
export interface RecordingSession {
  id: string;
  masterDeviceId: string;
  roomCode: string;
  status: SessionStatus;
  targetStartTime: number;
  targetStopTime?: number;
  createdAt: number;
  settings?: Record<string, unknown>;
  preview?: Record<string, unknown>;
  // Direct upload fields
  clapTime?: number; // When sync tone should play for audio alignment
  startTime?: number; // When recording actually started
  recordingId?: string; // Unique ID for this recording (for R2 paths)
  // Live preview streaming
  previewActiveDevice?: string; // Which camera device should be streaming preview
}

/**
 * Session device entity - device participation in a recording session
 */
export interface SessionDevice {
  id: string;
  sessionId: string;
  deviceId: string;
  role: DeviceRole;
  status: DeviceStatus;
  lastHeartbeat: number;
  appliedCommandId?: string;
  clockOffset?: number;
  capabilities?: Record<string, unknown>;
  deviceName?: string;
  errorMessage?: string;
  // Direct upload fields
  snapshotRequested?: number; // Timestamp when snapshot was requested
  battery?: number; // Device battery level (0-100)
  codec?: string; // Recording codec used (e.g., 'video/webm;codecs=vp9')
  chunksUploaded?: number; // Number of chunks uploaded
}

/**
 * Session snapshot entity - preview images from camera devices
 */
export interface SessionSnapshot {
  id: string;
  sessionId: string;
  deviceId: string;
  imageData: string; // Base64 JPEG
  timestamp: number;
}

/**
 * Theme options for user settings
 */
export type ThemeOption = "light" | "dark" | "system";

/**
 * Style variation options (visual style)
 */
export type StyleVariationOption = "clean" | "engaging" | "cinematic" | "hype";

/**
 * User settings entity - synced across devices
 */
export interface UserSettings {
  id: string;
  userId: string;
  theme: ThemeOption;
  styleVariation: StyleVariationOption;
  updatedAt: number;
}

// ============ Billing Types ============

/**
 * Subscription status
 */
export type SubscriptionStatus =
  | "active"
  | "canceled"
  | "past_due"
  | "trialing"
  | "unpaid";

/**
 * Subscription entity - links users to Stripe subscriptions
 */
export interface Subscription {
  id: string;
  userId: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  status: SubscriptionStatus;
  tierId: string;
  currentPeriodStart: number;
  currentPeriodEnd: number;
  cancelAtPeriodEnd: boolean;
  createdAt: number;
  updatedAt: number;
}

/**
 * Subscription tier features configuration
 */
export interface TierFeatures {
  allowedProviders: string[];
  prioritySupport?: boolean;
  customModels?: boolean;
  apiAccess?: boolean;
}

/**
 * Subscription tier entity - configurable tier definitions
 */
export interface SubscriptionTier {
  id: string;
  name: string;
  displayName: string;
  description: string;
  stripePriceId?: string;
  monthlyCredits: number;
  rateLimitRpm: number;
  rateLimitTpm: number;
  features: TierFeatures;
  monthlyPriceCents: number;
  sortOrder: number;
  isActive: boolean;
  isDefault: boolean;
  createdAt: number;
  updatedAt: number;
}

// ============ Financial Analytics Types ============

/**
 * API provider names
 */
export type ApiProvider =
  | "openai"
  | "anthropic"
  | "openrouter"
  | "elevenlabs"
  | "workers_ai";

/**
 * API cost entry - tracks individual API call costs for dashboard analytics
 */
export interface ApiCost {
  id: string;
  userId: string;
  provider: ApiProvider;
  model: string; // "gpt-4o", "claude-3.5-sonnet", etc.
  tokens: number; // input + output tokens
  cost: number; // in cents
  timestamp: number;
  projectId?: string; // optional link to project
}

/**
 * Monthly summary entity - aggregated financial data for dashboard
 */
export interface MonthlySummary {
  id: string;
  month: string; // "2025-01"
  totalRevenue: number; // in cents
  totalCosts: number; // in cents
  activeSubscribers: number;
  videosProduced: number;
  apiCostsByProvider: Record<string, number>; // { openai: 1234, anthropic: 567, ... }
  createdAt: number;
  updatedAt: number;
}

// ============ Admin Entity Types ============

/**
 * Admin user entity - users with admin/management roles
 */
export interface AdminUser {
  id: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  roles: string[]; // role IDs
  createdAt: number;
  updatedAt: number;
}

/**
 * Admin role entity - role-based access control
 */
export interface AdminRole {
  id: string;
  name: string;
  description: string;
  scopes: string[]; // scope IDs
  createdAt: number;
  updatedAt: number;
}

/**
 * Admin app entity - OAuth applications
 */
export interface AdminApp {
  id: string;
  name: string;
  clientId: string;
  clientSecret: string;
  redirectUris: string[];
  createdAt: number;
  updatedAt: number;
}

/**
 * Admin org entity - organizations
 */
export interface AdminOrg {
  id: string;
  name: string;
  slug: string;
  createdAt: number;
}

/**
 * Admin scope entity - permission scopes
 */
export interface AdminScope {
  id: string;
  name: string;
  description: string;
}

// ============ Bug Report Types ============

/**
 * Bug report entity - in-app bug reports with screenshots
 */
export interface BugReport {
  id: string;
  title: string;
  description: string;
  originalScreenshotUrl: string;
  annotatedScreenshotUrl: string;
  pageUrl: string;
  userAgent: string;
  viewport: string;
  userId: string;
  timestamp: number;
  // Linear export tracking
  linearIssueId?: string;
  linearIssueUrl?: string;
  linearIssueIdentifier?: string;
  exportedAt?: number;
}

// ============ Schema Definition ============

/**
 * InstantDB schema for type-safe queries
 * Matches instant.schema.ts in project root
 */
const schema = i.graph(
  {
    // System users table (InstantDB built-in)
    $users: i.entity({
      email: i.string().optional(),
      imageURL: i.string().optional(),
      type: i.string().optional(),
    }),
    projects: i.entity({
      userId: i.string(),
      name: i.string(),
      description: i.string(),
      flow: i.string(),
      style: i.string(),
      duration: i.number(),
      orientation: i.string(),
      frameRate: i.string(),
      generateMusic: i.boolean(),
      script: i.string().optional(),
      cameraGuidance: i.json().optional(),
      status: i.string(),
      createdAt: i.number(),
      updatedAt: i.number(),
      thumbnail: i.string().optional(),
      outputR2Key: i.string().optional(),
    }),
    project_videos: i.entity({
      projectId: i.string(),
      r2Key: i.string(),
      fileName: i.string(),
      fileSize: i.number(),
      mimeType: i.string(),
      duration: i.number().optional(),
      order: i.number(),
      uploadStatus: i.string(),
      uploadProgress: i.number().optional(),
      errorMessage: i.string().optional(),
      createdAt: i.number(),
    }),
    processing_jobs: i.entity({
      projectId: i.string(),
      status: i.string(),
      progress: i.number(),
      message: i.string(),
      startedAt: i.number(),
      completedAt: i.number().optional(),
      outputR2Key: i.string().optional(),
      error: i.string().optional(),
      analyticsJson: i.json().optional(),
      edlJson: i.json().optional(),
      // External job tracking (for async webhook-based rendering)
      externalJobId: i.string().optional(),
      externalProvider: i.string().optional(),
      musicKey: i.string().optional(),
      userId: i.string().optional(),
    }),
    recording_sessions: i.entity({
      masterDeviceId: i.string(),
      roomCode: i.string(),
      status: i.string(),
      targetStartTime: i.number(),
      targetStopTime: i.number().optional(),
      createdAt: i.number(),
      settings: i.json().optional(),
      preview: i.json().optional(),
      // Direct upload fields
      clapTime: i.number().optional(),
      startTime: i.number().optional(),
      recordingId: i.string().optional(),
      // Live preview streaming
      previewActiveDevice: i.string().optional(),
    }),
    session_devices: i.entity({
      sessionId: i.string(),
      deviceId: i.string(),
      role: i.string(),
      status: i.string(),
      lastHeartbeat: i.number(),
      appliedCommandId: i.string().optional(),
      clockOffset: i.number().optional(),
      capabilities: i.json().optional(),
      deviceName: i.string().optional(),
      errorMessage: i.string().optional(),
      // Direct upload fields
      snapshotRequested: i.number().optional(),
      battery: i.number().optional(),
      codec: i.string().optional(),
      chunksUploaded: i.number().optional(),
    }),
    session_snapshots: i.entity({
      sessionId: i.string(),
      deviceId: i.string(),
      imageData: i.string(),
      timestamp: i.number(),
    }),
    user_settings: i.entity({
      userId: i.string(),
      theme: i.string(),
      styleVariation: i.string(),
      updatedAt: i.number(),
    }),
    analytics_sessions: i.entity({
      userId: i.string(),
      sessionId: i.string(),
      browser: i.string(),
      browserVersion: i.string(),
      deviceType: i.string(),
      platform: i.string(),
      osVersion: i.string(),
      screenWidth: i.number(),
      screenHeight: i.number(),
      appVersion: i.string(),
      buildDate: i.string(),
      buildTimestamp: i.number(),
      startedAt: i.number(),
      endedAt: i.number().optional(),
      durationSeconds: i.number().optional(),
      pageCount: i.number(),
      errorCount: i.number(),
    }),
    analytics_events: i.entity({
      sessionId: i.string(),
      userId: i.string(),
      eventType: i.string(),
      eventName: i.string(),
      eventData: i.json().optional(),
      timestamp: i.number(),
      routePath: i.string().optional(),
      previousRoutePath: i.string().optional(),
      timeOnPreviousPageSeconds: i.number().optional(),
      errorMessage: i.string().optional(),
      errorStack: i.string().optional(),
      componentStack: i.string().optional(),
    }),
    // Billing entities
    subscriptions: i.entity({
      userId: i.string(),
      stripeCustomerId: i.string(),
      stripeSubscriptionId: i.string(),
      status: i.string(), // 'active' | 'canceled' | 'past_due' | 'trialing' | 'unpaid'
      tierId: i.string(),
      currentPeriodStart: i.number(),
      currentPeriodEnd: i.number(),
      cancelAtPeriodEnd: i.boolean(),
      createdAt: i.number(),
      updatedAt: i.number(),
    }),
    subscription_tiers: i.entity({
      name: i.string(), // 'free', 'pro', 'enterprise'
      displayName: i.string(),
      description: i.string(),
      stripePriceId: i.string().optional(),
      monthlyCredits: i.number(),
      rateLimitRpm: i.number(),
      rateLimitTpm: i.number(),
      features: i.json(), // TierFeatures
      monthlyPriceCents: i.number(),
      sortOrder: i.number(),
      isActive: i.boolean(),
      isDefault: i.boolean(),
      createdAt: i.number(),
      updatedAt: i.number(),
    }),
    // New collections for InstantDB-first architecture
    notifications: i.entity({
      userId: i.string(),
      type: i.string(), // 'render_complete' | 'render_failed' | 'share_received' | 'comment' | 'system' | 'credit_warning' | 'credit_critical' | 'credit_depleted'
      title: i.string(),
      body: i.string(),
      data: i.json(), // { projectId?, jobId?, actionUrl? }
      read: i.boolean(),
      createdAt: i.number(),
    }),
    feature_flags: i.entity({
      name: i.string(),
      description: i.string(),
      enabled: i.boolean(),
      rolloutPercent: i.number(),
      allowedUserIds: i.json(), // string[]
      blockedUserIds: i.json(), // string[]
      metadata: i.json(), // { variants?, defaultVariant? }
      createdAt: i.number(),
      updatedAt: i.number(),
    }),
    user_presence: i.entity({
      userId: i.string(),
      status: i.string(), // 'online' | 'idle' | 'offline'
      currentProjectId: i.string().optional(),
      currentAction: i.string().optional(),
      lastSeen: i.number(),
      deviceInfo: i.json(), // { platform, appVersion }
    }),
    // AI Gateway user settings (managed by ai-gateway-proxy worker)
    user_ai_settings: i.entity({
      // id is the userId
      allowed_providers: i.json(), // string[]
      credits_remaining: i.number(),
      credits_total: i.number(),
      rate_limit_rpm: i.number(),
      rate_limit_tpm: i.number(),
      budget_limit: i.number(),
      budget_used: i.number(),
      budget_period_start: i.string(),
      webhook_url: i.string().optional(),
      last_alert_percent: i.number().optional(),
      subscription_tier: i.string(), // 'free' | 'pro' | 'enterprise'
      monthly_limit: i.number(),
      credits_used_this_month: i.number(),
      last_credit_reset: i.string(),
    }),
    // Financial analytics entities (for dashboard metrics)
    api_costs: i.entity({
      userId: i.string(),
      provider: i.string(),
      model: i.string(),
      tokens: i.number(),
      cost: i.number(),
      timestamp: i.number(),
      projectId: i.string().optional(),
    }),
    monthly_summaries: i.entity({
      month: i.string(),
      totalRevenue: i.number(),
      totalCosts: i.number(),
      activeSubscribers: i.number(),
      videosProduced: i.number(),
      apiCostsByProvider: i.json(),
      createdAt: i.number(),
      updatedAt: i.number(),
    }),
    // Bug reporting entities
    bug_reports: i.entity({
      title: i.string(),
      description: i.string(),
      originalScreenshotUrl: i.string(),
      annotatedScreenshotUrl: i.string(),
      pageUrl: i.string(),
      userAgent: i.string(),
      viewport: i.string(),
      userId: i.string(),
      timestamp: i.number(),
      // Linear export tracking
      linearIssueId: i.string().optional(),
      linearIssueUrl: i.string().optional(),
      linearIssueIdentifier: i.string().optional(),
      exportedAt: i.number().optional(),
    }),
    // Admin entities
    admin_users: i.entity({
      email: i.string(),
      name: i.string(),
      firstName: i.string().optional(),
      lastName: i.string().optional(),
      roles: i.json(), // string[]
      createdAt: i.number(),
      updatedAt: i.number(),
    }),
    admin_roles: i.entity({
      name: i.string(),
      description: i.string(),
      scopes: i.json(), // string[]
      createdAt: i.number(),
      updatedAt: i.number(),
    }),
    admin_apps: i.entity({
      name: i.string(),
      clientId: i.string(),
      clientSecret: i.string(),
      redirectUris: i.json(), // string[]
      createdAt: i.number(),
      updatedAt: i.number(),
    }),
    admin_orgs: i.entity({
      name: i.string(),
      slug: i.string(),
      createdAt: i.number(),
    }),
    admin_scopes: i.entity({
      name: i.string(),
      description: i.string(),
    }),
  },
  {
    projectVideosProject: {
      forward: { on: "project_videos", has: "one", label: "project" },
      reverse: { on: "projects", has: "many", label: "videos" },
    },
    processingJobsProject: {
      forward: { on: "processing_jobs", has: "one", label: "project" },
      reverse: { on: "projects", has: "many", label: "jobs" },
    },
    sessionDevicesSession: {
      forward: { on: "session_devices", has: "one", label: "session" },
      reverse: { on: "recording_sessions", has: "many", label: "devices" },
    },
    sessionSnapshotsSession: {
      forward: { on: "session_snapshots", has: "one", label: "session" },
      reverse: { on: "recording_sessions", has: "many", label: "snapshots" },
    },
    sessionSnapshotsDevice: {
      forward: { on: "session_snapshots", has: "one", label: "device" },
      reverse: { on: "session_devices", has: "many", label: "snapshots" },
    },
    analyticsEventsSession: {
      forward: { on: "analytics_events", has: "one", label: "session" },
      reverse: { on: "analytics_sessions", has: "many", label: "events" },
    },
    // Billing relationships
    subscriptionTier: {
      forward: { on: "subscriptions", has: "one", label: "tier" },
      reverse: {
        on: "subscription_tiers",
        has: "many",
        label: "subscriptions",
      },
    },
    // Financial analytics relationships
    apiCostsProject: {
      forward: { on: "api_costs", has: "one", label: "project" },
      reverse: { on: "projects", has: "many", label: "apiCosts" },
    },
  },
);

type Schema = typeof schema;

/**
 * Initialize InstantDB client with typed schema
 * Devtools are disabled by default, enable via Settings > Developer
 */
export const db = init<Schema>({
  appId: APP_ID,
  devtool: ENABLE_DEVTOOLS,
});

// Re-export commonly used functions for convenience
export const { useQuery, transact, tx, auth } = db;

// ============ Query Helpers ============

/**
 * Query to get a project with its videos and latest job
 */
export function useProjectWithDetails(projectId: string) {
  return useQuery({
    projects: {
      $: { where: { id: projectId } },
      videos: {},
      jobs: { $: { order: { serverCreatedAt: "desc" }, limit: 1 } },
    },
  });
}

/**
 * Query to get all projects for the current user
 */
export function useUserProjects(userId: string) {
  return useQuery({
    projects: {
      $: {
        where: { userId },
        order: { serverCreatedAt: "desc" },
      },
    },
  });
}

/**
 * Query to watch a processing job in real-time by project ID
 */
export function useProcessingJob(projectId: string) {
  return useQuery({
    processing_jobs: {
      $: {
        where: { projectId },
        order: { serverCreatedAt: "desc" },
        limit: 1,
      },
    },
  });
}

/**
 * Query to watch a single processing job by ID in real-time (for modal details)
 */
export function useProcessingJobById(jobId: string | null) {
  return useQuery(
    jobId
      ? {
          processing_jobs: {
            $: {
              where: { id: jobId },
            },
            project: {},
          },
        }
      : null,
  );
}

/**
 * Query to get user settings
 */
export function useUserSettings(userId: string | undefined) {
  return useQuery(
    userId
      ? {
          user_settings: {
            $: { where: { userId }, limit: 1 },
          },
        }
      : null,
  );
}

/**
 * Update or create user settings
 */
export async function upsertUserSettings(
  userId: string,
  settings: { theme?: ThemeOption; styleVariation?: StyleVariationOption },
) {
  // Query existing settings
  const { data } = await db.queryOnce({
    user_settings: {
      $: { where: { userId }, limit: 1 },
    },
  });

  const existing = data?.user_settings?.[0];

  if (existing) {
    // Update existing
    await transact(
      tx.user_settings[existing.id].update({
        ...settings,
        updatedAt: Date.now(),
      }),
    );
  } else {
    // Create new
    const id = crypto.randomUUID();
    await transact(
      tx.user_settings[id].update({
        userId,
        theme: settings.theme ?? "system",
        styleVariation: settings.styleVariation ?? "engaging",
        updatedAt: Date.now(),
      }),
    );
  }
}

// ============ Billing Query Helpers ============

/**
 * Query to get user's subscription with tier details
 */
export function useUserSubscription(userId: string | undefined) {
  return useQuery(
    userId
      ? {
          subscriptions: {
            $: { where: { userId }, limit: 1 },
            tier: {},
          },
        }
      : null,
  );
}

/**
 * Query to get all active subscription tiers
 */
export function useSubscriptionTiers() {
  return useQuery({
    subscription_tiers: {
      $: {
        where: { isActive: true },
        order: { sortOrder: "asc" },
      },
    },
  });
}

// ============ Notification Helpers ============

/**
 * Query unread notifications for a user
 */
export function useUnreadNotifications(userId: string | undefined) {
  return useQuery(
    userId
      ? {
          notifications: {
            $: {
              where: { userId, read: false },
              order: { serverCreatedAt: "desc" },
              limit: 50,
            },
          },
        }
      : null,
  );
}

/**
 * Query all notifications for a user
 */
export function useNotifications(userId: string | undefined, limit = 50) {
  return useQuery(
    userId
      ? {
          notifications: {
            $: {
              where: { userId },
              order: { serverCreatedAt: "desc" },
              limit,
            },
          },
        }
      : null,
  );
}

/**
 * Mark a notification as read
 */
export async function markNotificationRead(notificationId: string) {
  await transact(tx.notifications[notificationId].update({ read: true }));
}

/**
 * Mark all notifications as read for a user
 */
export async function markAllNotificationsRead(userId: string) {
  const { data } = await db.queryOnce({
    notifications: {
      $: { where: { userId, read: false } },
    },
  });

  const notifications = data?.notifications ?? [];
  if (notifications.length === 0) return;

  const updates = notifications.map((n) =>
    tx.notifications[n.id].update({ read: true }),
  );
  await transact(updates);
}

// ============ Feature Flag Helpers ============

/**
 * Query all feature flags
 */
export function useFeatureFlags() {
  return useQuery({ feature_flags: {} });
}

/**
 * Simple hash function for deterministic rollout
 */
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Check if a feature flag is enabled for a user
 */
export function isFeatureFlagEnabled(
  flag: FeatureFlag | undefined,
  userId: string | undefined,
): boolean {
  if (!flag) return false;
  if (!flag.enabled) return false;

  // Check blocked list
  if (userId && flag.blockedUserIds?.includes(userId)) return false;

  // Check allowed list (beta testers)
  if (userId && flag.allowedUserIds?.includes(userId)) return true;

  // Rollout percentage (deterministic based on user ID)
  if (flag.rolloutPercent < 100 && userId) {
    const hash = simpleHash(userId + flag.name);
    if (hash % 100 >= flag.rolloutPercent) return false;
  }

  return true;
}

/**
 * Create or update a feature flag
 */
export async function upsertFeatureFlag(
  flagId: string,
  data: Partial<Omit<FeatureFlag, "id">>,
) {
  await transact(
    tx.feature_flags[flagId].update({
      ...data,
      updatedAt: Date.now(),
    }),
  );
}

// ============ User Presence Helpers ============

/**
 * Query online users (for a specific project or globally)
 */
export function useOnlineUsers(projectId?: string) {
  return useQuery({
    user_presence: {
      $: {
        where: projectId
          ? { currentProjectId: projectId, status: "online" }
          : { status: "online" },
      },
    },
  });
}

/**
 * Update user presence
 */
export async function updatePresence(
  userId: string,
  updates: Partial<Omit<UserPresence, "id" | "userId">>,
) {
  await transact(
    tx.user_presence[userId].update({
      userId,
      ...updates,
      lastSeen: Date.now(),
    }),
  );
}

// ============ Processing Jobs Helpers ============

/**
 * Query all processing jobs with project details (for admin view)
 * Real-time subscription using useQuery
 */
export function useProcessingJobs(limit = 100) {
  return useQuery({
    processing_jobs: {
      $: {
        order: { serverCreatedAt: "desc" },
        limit,
      },
      project: {},
    },
  });
}

/**
 * Query processing jobs by status
 */
export function useProcessingJobsByStatus(
  status: JobStatus | null,
  limit = 100,
) {
  if (status === null) {
    return useProcessingJobs(limit);
  }
  return useQuery({
    processing_jobs: {
      $: {
        where: { status },
        order: { serverCreatedAt: "desc" },
        limit,
      },
      project: {},
    },
  });
}

/**
 * Get job stats for dashboard cards
 */
export async function getJobStats() {
  const { data } = await db.queryOnce({
    processing_jobs: {},
  });

  const jobs = data?.processing_jobs ?? [];
  const now = Date.now();
  const oneDayAgo = now - 24 * 60 * 60 * 1000;

  return {
    queued: jobs.filter((j) => j.status === "queued").length,
    active: jobs.filter(
      (j) =>
        j.status === "uploading" ||
        j.status === "analyzing" ||
        j.status === "rendering",
    ).length,
    completedToday: jobs.filter((j) => {
      const completedAt = j.completedAt as number | undefined;
      return j.status === "completed" && completedAt && completedAt > oneDayAgo;
    }).length,
    failedToday: jobs.filter((j) => {
      const completedAt = j.completedAt as number | undefined;
      return j.status === "failed" && completedAt && completedAt > oneDayAgo;
    }).length,
  };
}

// ============ Bug Report Helpers ============

/**
 * Query all bug reports (for admin view)
 */
export function useBugReports() {
  return useQuery({
    bug_reports: {
      $: {
        order: { serverCreatedAt: "desc" },
      },
    },
  });
}

/**
 * Query bug reports by export status
 */
export function useBugReportsByStatus(exported: boolean | null) {
  if (exported === null) {
    return useBugReports();
  }
  // Note: InstantDB doesn't support "exists" queries, so we query all and filter client-side
  return useBugReports();
}

/**
 * Create a new bug report
 */
export async function createBugReport(
  data: Omit<
    BugReport,
    | "id"
    | "linearIssueId"
    | "linearIssueUrl"
    | "linearIssueIdentifier"
    | "exportedAt"
  >,
): Promise<string> {
  const reportId = id();
  await transact(
    tx.bug_reports[reportId].update({
      ...data,
    }),
  );
  return reportId;
}

/**
 * Update a bug report with Linear export info
 */
export async function updateBugReportLinearExport(
  reportId: string,
  linearData: {
    linearIssueId: string;
    linearIssueUrl: string;
    linearIssueIdentifier: string;
  },
) {
  await transact(
    tx.bug_reports[reportId].update({
      ...linearData,
      exportedAt: Date.now(),
    }),
  );
}

// ============ Admin Query Helpers ============

/**
 * Query all admin users with optional search filter
 */
export function useAdminUsers(search?: string) {
  const result = useQuery({ admin_users: {} });

  // Type for admin users from InstantDB
  type AdminUserRecord = {
    id: string;
    email: string;
    name: string;
    firstName?: string;
    lastName?: string;
    roles: unknown;
    createdAt: number;
    updatedAt: number;
  };

  // Client-side filtering since InstantDB doesn't support LIKE queries
  const users = (result.data?.admin_users ?? []) as AdminUserRecord[];
  if (search && users.length > 0) {
    const s = search.toLowerCase();
    const filtered = users.filter(
      (u) =>
        u.email?.toLowerCase().includes(s) ||
        u.name?.toLowerCase().includes(s) ||
        u.firstName?.toLowerCase().includes(s) ||
        u.lastName?.toLowerCase().includes(s),
    );
    return {
      ...result,
      data: { admin_users: filtered },
    };
  }

  return result;
}

/**
 * Query all admin roles
 */
export function useAdminRoles() {
  return useQuery({ admin_roles: {} });
}

/**
 * Query all admin apps
 */
export function useAdminApps() {
  return useQuery({ admin_apps: {} });
}

/**
 * Query all admin orgs
 */
export function useAdminOrgs() {
  return useQuery({ admin_orgs: {} });
}

/**
 * Query all admin scopes
 */
export function useAdminScopes() {
  return useQuery({ admin_scopes: {} });
}

/**
 * Create a new admin role
 */
export async function createAdminRole(
  data: Omit<AdminRole, "id" | "createdAt" | "updatedAt">,
): Promise<string> {
  const roleId = id();
  const now = Date.now();
  await transact(
    tx.admin_roles[roleId].update({
      ...data,
      createdAt: now,
      updatedAt: now,
    }),
  );
  return roleId;
}

/**
 * Update an admin role
 */
export async function updateAdminRole(
  roleId: string,
  data: Partial<Omit<AdminRole, "id" | "createdAt">>,
) {
  await transact(
    tx.admin_roles[roleId].update({
      ...data,
      updatedAt: Date.now(),
    }),
  );
}

/**
 * Delete an admin role
 */
export async function deleteAdminRole(roleId: string) {
  await transact(tx.admin_roles[roleId].delete());
}

/**
 * Create a new admin app
 */
export async function createAdminApp(
  data: Omit<AdminApp, "id" | "createdAt" | "updatedAt">,
): Promise<string> {
  const appId = id();
  const now = Date.now();
  await transact(
    tx.admin_apps[appId].update({
      ...data,
      createdAt: now,
      updatedAt: now,
    }),
  );
  return appId;
}

/**
 * Update an admin app
 */
export async function updateAdminApp(
  appId: string,
  data: Partial<Omit<AdminApp, "id" | "createdAt">>,
) {
  await transact(
    tx.admin_apps[appId].update({
      ...data,
      updatedAt: Date.now(),
    }),
  );
}

/**
 * Delete an admin app
 */
export async function deleteAdminApp(appId: string) {
  await transact(tx.admin_apps[appId].delete());
}

/**
 * Create a new admin user
 */
export async function createAdminUser(
  data: Omit<AdminUser, "id" | "createdAt" | "updatedAt">,
): Promise<string> {
  const userId = id();
  const now = Date.now();
  await transact(
    tx.admin_users[userId].update({
      ...data,
      createdAt: now,
      updatedAt: now,
    }),
  );
  return userId;
}

/**
 * Update an admin user
 */
export async function updateAdminUser(
  userId: string,
  data: Partial<Omit<AdminUser, "id" | "createdAt">>,
) {
  await transact(
    tx.admin_users[userId].update({
      ...data,
      updatedAt: Date.now(),
    }),
  );
}

/**
 * Delete an admin user
 */
export async function deleteAdminUser(userId: string) {
  await transact(tx.admin_users[userId].delete());
}

// ============ AI Credits Query Helpers ============

/**
 * Query all user AI settings (for admin view)
 */
export function useAllUserAISettings() {
  return useQuery({
    user_ai_settings: {},
  });
}
